import { computed, watch } from 'vue';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useApi, useToaster, ClientReviewSchema } from '@shopexperts/shared';
import { z } from 'zod';

type ClientReview = z.infer<typeof ClientReviewSchema>;

/**
 * Composable for fetching reviews submitted by the authenticated client.
 * 
 * Uses the client reviews API to retrieve a paginated list of reviews
 * that the client has submitted for experts.
 * 
 * @example
 * ```ts
 * const { reviews, isLoading, refetch } = useClientReviews();
 * ```
 */
export function useClientReviews() {
  const api = useApi();
  const toaster = useToaster();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['client', 'reviews'],
    queryFn: async ({ pageParam }) => {
      return api.client.reviewsV2.getReviews({
        query: { limit: 20, cursor: pageParam },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  watch(error, (newError) => {
    if (newError) {
      toaster.create({
        title: 'Failed to load reviews',
        description: newError.message || 'An error occurred while fetching your reviews.',
        type: 'error',
      });
      console.error('[useClientReviews] Error:', newError);
    }
  });

  const reviews = computed<ClientReview[]>(() => {
    return data.value?.pages.flatMap((page) => page.data) ?? [];
  });

  return {
    reviews,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  };
}

